"use client";

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ContactData } from '@/data/types';
import { submitContactForm } from '@/app/[lang]/contact/actions';
import { useData } from '@/i18n/LangContext';

interface ContactFormProps {
  formLabels: ContactData['formLabels'];
  formPlaceholders: ContactData['formPlaceholders'];
  successMessage: string | {
    title: string;
    message: string;
    newFormButton: string;
  };
  errorMessage: string;
}

export function ContactForm({
                              formLabels,
                              formPlaceholders,
                              successMessage,
                              errorMessage
                            }: ContactFormProps) {
  const { ui } = useData();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [animationKey, setAnimationKey] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData(e.currentTarget);
      const result = await submitContactForm(formData);

      if (result.success) {
        setIsSubmitted(true);
      } else {
        // Fehlercode der Server Action clientseitig auf ui.contact mappen
        setError(result.errorCode === 'validation'
          ? ui.contact.errorValidation
          : (ui.contact.errorGeneric || errorMessage));
      }
    } catch (err) {
      console.error("Fehler beim Formular-Submit:", err);
      setError(ui.contact.errorGeneric || errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setTimeout(() => {
      setAnimationKey(prev => prev + 1);
    }, 100);
  };

  // Extrahiere Titel und Nachricht aus successMessage oder verwende Default-Werte
  const getSuccessTitle = () => {
    if (typeof successMessage === 'object' && successMessage.title) {
      return successMessage.title;
    }
    return ui.contact.successTitle;
  };

  const getSuccessMessage = () => {
    if (typeof successMessage === 'object' && successMessage.message) {
      return successMessage.message;
    }
    return typeof successMessage === 'string' ? successMessage : ui.contact.successText;
  };

  const getNewFormButtonText = () => {
    if (typeof successMessage === 'object' && successMessage.newFormButton) {
      return successMessage.newFormButton;
    }
    return ui.contact.newFormButton;
  };

  return (
    <AnimatePresence mode="wait" initial={false}>
      {isSubmitted ? (
        <motion.div
          key="success"
          data-cy="form-success"
          className="bg-[rgba(90,21,16,0.2)] backdrop-blur-sm border border-[#ff8855]/20 rounded-xl p-6 md:p-8 flex flex-col items-center justify-center min-h-[400px]"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.4 }}
        >
          <motion.div
            className="w-20 h-20 mx-auto mb-6 text-[#ff8855]"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', damping: 12, delay: 0.2 }}
          >
            <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
              <motion.circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
              <motion.path
                d="M8 12l3 3 5-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
              />
            </svg>
          </motion.div>

          <motion.h2
            className="text-2xl md:text-3xl font-bold mb-4 text-center text-[#ff8855]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {getSuccessTitle()}
          </motion.h2>

          <motion.p
            className="text-lg mb-8 text-center text-white/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            {getSuccessMessage()}
          </motion.p>

          <motion.button
            onClick={resetForm}
            className="px-6 py-3 rounded-lg bg-[#5A1510] text-white font-medium hover:bg-[#7A2520] transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            {getNewFormButtonText()}
          </motion.button>
        </motion.div>
      ) : (
        // Formular mit Key für Animation-Reset
        <motion.div
          key={`form-${animationKey}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ type: 'spring', damping: 15 }}
          className="bg-[rgba(90,21,16,0.2)] backdrop-blur-sm border border-[#ff8855]/20 rounded-xl p-6 md:p-8"
        >
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div
                  className="group"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <label htmlFor="name" className="block text-[#ff8855] font-medium mb-2">
                    {formLabels.name} <span className="text-[#ff8855]">*</span>
                  </label>
                  <div className="relative overflow-hidden rounded-lg">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,136,85,0.2)] rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#ff8855] focus:ring-1 focus:ring-[#ff8855] transition-colors"
                      placeholder={formPlaceholders.name}
                    />
                    <motion.div
                      className="absolute bottom-0 left-0 h-0.5 bg-[#ff8855] w-0 group-hover:w-full"
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </motion.div>

                <motion.div
                  className="group"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <label htmlFor="email" className="block text-[#ff8855] font-medium mb-2">
                    {formLabels.email} <span className="text-[#ff8855]">*</span>
                  </label>
                  <div className="relative overflow-hidden rounded-lg">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,136,85,0.2)] rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#ff8855] focus:ring-1 focus:ring-[#ff8855] transition-colors"
                      placeholder={formPlaceholders.email}
                    />
                    <motion.div
                      className="absolute bottom-0 left-0 h-0.5 bg-[#ff8855] w-0 group-hover:w-full"
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </motion.div>
              </div>

              <motion.div
                className="group"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <label htmlFor="subject" className="block text-[#ff8855] font-medium mb-2">
                  {formLabels.subject} <span className="text-[#ff8855]">*</span>
                </label>
                <div className="relative overflow-hidden rounded-lg">
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,136,85,0.2)] rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#ff8855] focus:ring-1 focus:ring-[#ff8855] transition-colors"
                    placeholder={formPlaceholders.subject}
                  />
                  <motion.div
                    className="absolute bottom-0 left-0 h-0.5 bg-[#ff8855] w-0 group-hover:w-full"
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </motion.div>

              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  opacity: 0,
                  pointerEvents: 'none'
                }}
              >
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <motion.div
                className="group"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <label htmlFor="message" className="block text-[#ff8855] font-medium mb-2">
                  {formLabels.message} <span className="text-[#ff8855]">*</span>
                </label>
                <div className="relative overflow-hidden rounded-lg">
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,136,85,0.2)] rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#ff8855] focus:ring-1 focus:ring-[#ff8855] transition-colors resize-y"
                    placeholder={formPlaceholders.message}
                  />
                  <motion.div
                    className="absolute bottom-0 left-0 h-0.5 bg-[#ff8855] w-0 group-hover:w-full"
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </motion.div>

              {error && (
                <motion.div
                  data-cy="form-error"
                  className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <p className="text-red-500">{error}</p>
                </motion.div>
              )}

              <motion.div
                className="flex items-center justify-between"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <p className="text-sm text-white/60">
                  <span className="text-[#ff8855]">{ui.contact.requiredField.slice(0, 1)}</span>{ui.contact.requiredField.slice(1)}
                </p>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center relative overflow-hidden px-6 py-3 rounded-lg bg-[#5A1510] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{
                    scale: 1.03,
                    backgroundColor: 'rgba(120, 30, 20, 1)'
                  }}
                  whileTap={{ scale: 0.97 }}
                >
                  {!isSubmitting ? (
                    <motion.span>
                      {formLabels.submit}
                    </motion.span>
                  ) : (
                    <motion.div
                      className="flex items-center space-x-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <motion.div
                        className="w-4 h-4 border-2 border-t-transparent border-white rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                      />
                      <span>{ui.contact.sending}</span>
                    </motion.div>
                  )}
                </motion.button>
              </motion.div>
            </div>
          </form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}